<template>
    <products-layout>
        <div class="backoffice-categories">
            <div class="page-header">
                <div class="left">
                    <h2 class="page-title">{{ $page.props.lang.title || "Categories List" }}</h2>
                </div>
                <div class="right">
                    <v-btn @click="newCategoryModal = true" color="primary" dark>
                    <v-icon small>mdi-plus</v-icon>
                    <span v-if="$vuetify.breakpoint.mdAndUp" class="ml-2">{{ $page.props.lang.newCategory || "New category" }}</span>
                    </v-btn>
                </div>
            </div>
            <div class="filter-section">
                <v-text-field v-model="form.search" :label="$page.props.lang.filter_search || 'Search'" background-color="#FFFFFF" outlined dense prepend-inner-icon="mdi-magnify" class="table-search"></v-text-field>
            </div>
            <v-dialog v-model="newCategoryModal" max-width="500px">
                <v-card>
                    <div class="modal-header">
                        <h4 class="modal-headline">{{ $page.props.lang.newCategory || "New category" }}</h4>
                        <div class="tools">
                            <v-btn icon @click="newCategoryModal = false"><v-icon>mdi-close</v-icon></v-btn>
                        </div>
                    </div>
                    <v-card-text>
                        <v-container>
                                <v-row>
                                    <v-col cols="8">    
                                        <p class="custom-label">{{ $page.props.lang.name || 'Category name' }}</p>
                                        <v-text-field v-model="newCategory.name" 
                                        :error-messages="responseErrors.name" :hide-details="!responseErrors.name"
                                        @focus="responseErrors.name=''" outlined dense></v-text-field>
                                    </v-col> 
                                    <v-col cols="4">
                                        <v-btn @click="addCategory" color="primary" class="mt-6 ml-8">{{ $page.props.lang.save || "Save" }}</v-btn>
                                    </v-col>    
                                </v-row>
                        </v-container>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <div class="table-container">
                <v-data-table
                    :headers="categoriesHeaders"
                    :items="categoriesData.data"
                    item-key="item.id"
                    mobile-breakpoint="0"
                    hide-default-footer
                    >
                    <template v-slot:item.name="{ item }">
                        {{ item.name }}
                    </template>
                    <template v-slot:item.action="{ item }">
                            <v-btn @click="openEditCategory(item.id)" :id="'edit' + item.id" icon>
                                <v-icon>mdi-pencil-outline</v-icon>
                                <v-tooltip :activator="'#edit'+ item.id" bottom open-delay="500">
                                    <span>{{$page.props.lang.edit || "Edit"}}</span>
                                </v-tooltip>
                        </v-btn>
                        <v-btn @click="openDeleteModal(item.id)" :id="'delete' + item.id" class="delete-icon" icon>
                                <v-icon>mdi-trash-can-outline</v-icon>
                                <v-tooltip :activator="'#delete'+ item.id" bottom open-delay="500">
                                    <span>{{$page.props.lang.delete || "Delete"}}</span>
                                </v-tooltip>
                        </v-btn>
                    </template>
                </v-data-table>
                <ConfirmModal 
                    :title="$page.props.lang.confirmModalTitle || 'Confirmation'" 
                    :description="$page.props.lang.delete_confirmation_description || 'If you click YES, the category will be permanently deleted. Are you sure?'" 
                    :open="deleteModal" 
                    @no="deleteModal = false" 
                    @yes="deleteCategory" 
                    @close="deleteModal = false">
                </ConfirmModal>
                <pagination :links="categories.links" />
            </div>
            <v-dialog v-model="editCategoryModal" max-width="500px">
                <v-card>
                    <div class="modal-header">
                        <h4 class="modal-headline">{{ $page.props.lang.editCategory || "Edit category" }}</h4>
                        <div class="tools">
                            <v-btn icon @click="editCategoryModal = false"><v-icon>mdi-close</v-icon></v-btn>
                        </div>
                    </div>
                    <v-card-text>
                        <v-container>
                                <v-row>
                                    <v-col cols="8">    
                                        <p class="custom-label">{{ $page.props.lang.name || 'Category name' }}</p>
                                        <v-text-field v-model="categoryForEdit.name" 
                                        :error-messages="responseErrors.name" :hide-details="!responseErrors.name"
                                        @focus="responseErrors.name=''" outlined dense></v-text-field>
                                    </v-col> 
                                    <v-col cols="4">
                                        <v-btn @click="editCategory" color="primary" class="mt-6 ml-8">{{ $page.props.lang.save || "Save" }}</v-btn>
                                    </v-col>    
                                </v-row>
                        </v-container>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </div>
    </products-layout>
</template>

<script>
import ProductsLayout from '@/js/Layouts/ProductsLayout'
import Pagination from "@/js/Components/Pagination"
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import EventBus from '@/js/eventbus'
import ConfirmModal from '@/js/Components/ConfirmModal'

export default {
    metaInfo() {
        return {
            title: this.$page.props.lang.title || "List of Categories"
        }
    },
    components: {
        ProductsLayout,
        Pagination,
        ConfirmModal
    },
    props: {
        categories: Object,
        filters: Object,
        lang: Object,
        locale: String
    },
    data() {
        return {
            form: {
                search: this.filters.search,
            },
            categoriesData: this.categories,
            categoriesHeaders: [
                {text: this.lang.name || 'Name', value: 'name'},
                {text: this.lang.action || 'Action', value: 'action', align: 'end', sortable: false}
            ],
            categoryForEdit: {},
            editCategoryModal: false,
            editCategoryMessage: {},
            newCategory: {
                name:'',
                type:'product'
            },
            newCategoryModal: false,
            categoryAddMessage: {},
            deleteModal: false,
            categoryForDeleteId: null,
            responseErrors:{}
        }
    },
    watch: {
        form: {
            handler: throttle(function() {
                let query = pickBy(this.form)
                this.$inertia.replace("/backoffice/categories?"+(Object.keys(query).length ? "search="+query.search : "remember=forget"))
                .then(() => {
                    this.categoriesData = this.categories
                })            
            }, 250),
            deep: true,
        },
        editCategoryModal: function(value) {
            if (value===false) {
                this.responseErrors = {}
            }
        }
    },
    methods: {
        openEditCategory(itemId) {
            this.responseErrors = {};
            axios.get('/backoffice/categories/getCategory/'+ itemId)
                .then(response => {
                    if(response.data.status === "OK"){
                        this.editCategoryModal = true;
                        this.categoryForEdit = response.data.category
                    }else{
                        alert(response.data.message)
                    }
                });
        },
        editCategory() {
            axios.put('/backoffice/categories/update', this.categoryForEdit)
                .then(response => {              
                    if (response.data.status==="OK") {
                        this.editCategoryModal = false;
                        this.$inertia.visit('/backoffice/categories', {only: ['categories']}); 
                        EventBus.$emit('SHOW_NOTIFICATION',{  
                            message: response.data.message,
                            color: 'success',
                            timeout: 5000
                        })
                    }
                })
                .catch(error => {
                     EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
        },
        openDeleteModal(itemId) {
            this.deleteModal = true
            this.categoryForDeleteId = itemId
        },
        deleteCategory() {
            axios.post('/backoffice/categories/delete', {id: this.categoryForDeleteId})
                .then(response => {                  
                    if(response.data.status==="OK") {
                        this.categoriesData.data.splice(this.categoriesData.data.findIndex(i => i.id === this.categoryForDeleteId), 1)
                        EventBus.$emit('SHOW_NOTIFICATION',{ 
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }
                })
                .catch(function (error) {
                    EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
            this.deleteModal = false
        },
        addCategory() {
            axios.post('/backoffice/categories/store', this.newCategory)
                .then(response => {                
                    if (response.data.status==="OK") {
                        this.newCategoryModal = false;
                        this.$inertia.visit('/backoffice/categories', {only: ['categories']}); 
                        EventBus.$emit('SHOW_NOTIFICATION',{  
                            message: response.data.message,
                            color: 'success',
                            timeout: 5000
                        })
                    }
                })
                .catch(error => {            
                    EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                });
        }
    }
}
</script>